import { LoginScreen } from '../services/models/login-screen.model';
import {
  Action, NgxsOnInit,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import {
  ChangeScreen,
  CloseDialog, ExternalLogin,
  ChangeLastScreen,
  Login,
  OpenDialog,
  RecoverPassword, Signup, ValidateCode,
  VerifyReCaptcha,
} from './login.actions';
import * as UserActions from '../../_core/state/user/user.actions';
import { patch } from '@ngxs/store/operators';
import { LoginService } from '../services/login.service';
import { tap, throwError } from 'rxjs';
import { LoadingModel } from '../../_core/state/loading.model';
import { catchError, finalize } from 'rxjs/operators';
import { VISITOR_ID } from '../../models/device';
import { BroadcastService } from '../../services/broadcast.service';
import { ConfigService } from '../../services/config.service';
import { IdentifierType, ParameterType, RegisterParameter } from '../../models/client-configuration';
import dayjs from 'dayjs';
import { AnalyticsService } from '../../services/analytics.service';
import { SignupStatus } from '../services/models/signup.response';
import { AuthService } from '../../services/auth.service';
import { ToastService } from '../../_core/services/toast.service';
import { UrlStateService } from '../../services/url-state.service';
import { UserState, type UserStateModel } from 'src/app/_core/state/user/user.state';
import { User } from 'src/app/models/user';
import { createWindowDeferred } from '../../_core/utils/create-window-deferred';

export type LoginStateModel = LoadingModel & {
  open: boolean,
  screen: LoginScreen,
  reCaptchaResponse: boolean | null,
  recoverPasswordResponse: boolean | null, // TODO: Remove this when opac-dialog will be implemented
  loginAttempts: number,
  code: string | null,
  signupStatus: SignupStatus | null,
  lastScreen: string,
};

export const initialState: LoginStateModel = {
  isLoading: false,
  open: false,
  screen: 'login',
  reCaptchaResponse: null,
  recoverPasswordResponse: null,
  loginAttempts: 0,
  code: null,
  signupStatus: null,
  lastScreen: '',
};

@State<LoginStateModel>({
  name: 'login',
  defaults: initialState,
})
@Injectable()
export class LoginState implements NgxsOnInit {
  private static LOGIN_ATTEMPTS_STORAGE_KEY = 'attempts';

  private readonly configService = inject(ConfigService);
  private readonly loginService = inject(LoginService);
  private readonly broadcastService = inject(BroadcastService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly urlStateService = inject(UrlStateService);

  ngxsOnInit(context: StateContext<LoginStateModel>) {
    context.setState(
      patch({ loginAttempts: Number(localStorage.getItem(LoginState.LOGIN_ATTEMPTS_STORAGE_KEY) ?? '0') }),
    );
  }

  @Selector()
  static open(state: LoginStateModel) {
    return state.open;
  }

  @Selector()
  static screen(state: LoginStateModel) {
    return state.screen;
  }

  @Selector()
  static lastScreen(state: LoginStateModel) {
    return state.lastScreen;
  }

  @Selector()
  static reCaptchaResponse(state: LoginStateModel) {
    return state.reCaptchaResponse;
  }

  @Selector()
  static recoverPasswordResponse(state: LoginStateModel) {
    return state.recoverPasswordResponse;
  }

  @Selector()
  static loginAttempts(state: LoginStateModel) {
    return state.loginAttempts;
  }

  @Selector()
  static signupStatus(state: LoginStateModel) {
    return state.signupStatus;
  }

  @Selector([UserState.user])
  static isAuthenticated(_: LoginStateModel, user: User) {
    return user !== undefined;
  }

  @Action(OpenDialog)
  openDialog(context: StateContext<LoginStateModel>, { screen }: OpenDialog) {
    context.setState(
      patch({
        open: true,
      }),
    );

    context.dispatch(new ChangeScreen(screen));
  }

  @Action(CloseDialog)
  closeDialog(context: StateContext<LoginStateModel>) {
    context.setState(
      patch({
        reCaptchaResponse: null,
        recoverPasswordResponse: null,
        open: false,
        screen: 'login',
        isLoading: false,
        code: null,
        lastScreen: '',
      }),
    );
  }

  @Action(ChangeScreen)
  changeScreen(context: StateContext<LoginStateModel>, { screen }: ChangeScreen) {
    const currentScreen = context.getState().screen;
    let resolvedScreen = screen;

    switch (screen) {
      case 'signup':
        resolvedScreen = this.getSignupScreen(context, currentScreen);
        break;
    }

    context.setState(
      patch({
        screen: resolvedScreen,
        reCaptchaResponse: null,
        recoverPasswordResponse: null,
      }),
    );
  }

  @Action(ChangeLastScreen)
  changeLastScreen(context: StateContext<LoginStateModel>, { screen }: ChangeLastScreen) {
    context.setState(
      patch({
        lastScreen: screen,
      }),
    );
  }

  @Action(VerifyReCaptcha)
  verifyReCaptcha(context: StateContext<LoginStateModel>, { token }: VerifyReCaptcha) {
    context.setState(patch({
      isLoading: true,
      reCaptchaResponse: null,
    }));

    return this.loginService.verifyReCaptcha(token).pipe(
      tap((response) => {
        context.setState(
          patch({
            reCaptchaResponse: response,
          }),
        );
      }),
      finalize(() => {
        context.setState(
          patch({ isLoading: false }),
        );
      }),
    );
  }

  @Action(RecoverPassword)
  recoverPassword(context: StateContext<LoginStateModel>, { identifier }: RecoverPassword) {
    context.setState(patch({ isLoading: true }));

    return this.loginService.recoverPassword(identifier).pipe(
      tap(() => {
        this.toastService.showToast('messages.login.email', 'info');

        // TODO: Replace this with open: false when opac-dialog will be implemented
        context.setState(
          patch({
            recoverPasswordResponse: true,
          })
        )
      }),
      finalize(() => {
        context.setState(
          patch({ isLoading: false }),
        );
      }),
    );
  }

  @Action(Login)
  login(context: StateContext<LoginStateModel>, { request }: Login) {
    context.setState(patch({ isLoading: true }));

    return this.loginService.login(request).pipe(
      tap((user) => {
        localStorage.setItem(VISITOR_ID, request.visitorId);
        localStorage.removeItem(LoginState.LOGIN_ATTEMPTS_STORAGE_KEY);

        this.broadcastService.broadcastLogin(true);
        this.toastService.showToast('messages.login.success');

        context.dispatch(new UserActions.SaveUser(user));
        context.dispatch(new UserActions.LoadUserProfilePicture(user));
      }),
      catchError((error) => {
        const currentAttempts = context.getState().loginAttempts;
        context.setState(
          patch({
            loginAttempts: currentAttempts + 1,
          })
        );
        localStorage.setItem(LoginState.LOGIN_ATTEMPTS_STORAGE_KEY, (currentAttempts + 1).toString());

        return throwError(() => error);
      }),
      finalize(() => {
        context.setState(
          patch({ isLoading: false }),
        );
      }),
    );
  }

  @Action(ValidateCode)
  validateCode(context: StateContext<LoginStateModel>, { code }: ValidateCode) {
    context.setState(patch({ isLoading: true }));

    return this.loginService.validateCode(code).pipe(
      tap(() => {
        context.setState(
          patch({ code }),
        );

        context.dispatch(new ChangeScreen('signup'));
      }),
      finalize(() => {
        context.setState(
          patch({ isLoading: false }),
        );
      }),
    )
  }

  @Action(Signup)
  signup(context: StateContext<LoginStateModel>, { formModel, multipleId }: Signup) {
    context.setState(patch({ isLoading: true }));

    /** Handle modification of initial form params */
    const userParams: RegisterParameter[] = Object.keys(formModel).map((k) => {
      let value = formModel[k];

      if (value) {
        const key = Number(k);

        if (key === ParameterType.IDENTIFICADOR_EXTERNO) {
          value = value[k].field + '#_#' + value[k]['data-register'];
        } else if (key === ParameterType.CONTRASENNA) {
          value = value[k].data;
        } else if (key === ParameterType.FECHA_NACIMIENTO) {
          value = dayjs(value).format('DD/MM/YYYY');
        }
      }

      return { type: ParameterType[k], value };
    });

    const loginType = formModel[ParameterType.IDENTIFICADOR_EXTERNO][0];

    if (loginType.field === IdentifierType.EMAIL.toString()) {
      userParams.push({
        type: ParameterType.CORREO_ELECTRONICO as ParameterType,
        value: loginType['data-register'],
      });
    }

    return this.loginService.signup(userParams, multipleId, context.getState().code).pipe(
      tap(({ userId, status }) => {
        switch (status) {
          case 'OK':
            this.toastService.showToast('messages.registration.success', 'success', {
              id: userId,
            });
            context.dispatch(new ChangeScreen('login'));
            break;
          case 'NEEDS_VALIDATION':
            this.toastService.showToast('messages.registration.needsValidation');
            context.dispatch(new ChangeScreen('login'));
            break;
          case 'NEEDS_APPROVAL':
            this.toastService.showToast('messages.registration.needsApproval', 'info');
            break;
          case 'NEEDS_ACCOUNT_VALIDATION':
            this.toastService.showToast('messages.registration.needsAccountValidation', 'info');
            context.dispatch(new ChangeScreen('login'));
            break;
        }

        this.analyticsService.sendSignupOkEvent();
        context.setState(patch({ signupStatus: status }));
      }),
      finalize(() => {
        context.setState(
          patch({ isLoading: false })
        );
      }),
    )
  }

  @Action(ExternalLogin)
  externalLogin(context: StateContext<LoginStateModel>, { url }: ExternalLogin) {
    const [ openWindow ] = createWindowDeferred();
    context.setState(patch({ isLoading: true }));

    // This method uses ssoType that is setted into service, when it won't exist, then replace by own loginService method.
    return this.authService.getExternalLoginUrl(url).pipe(
      tap((externalUrl) => {
        openWindow(externalUrl);
      }),
      finalize(() => {
        context.setState(
          patch({ isLoading: false })
        );
      }),
    );
  }

  private getSignupScreen(context: StateContext<LoginStateModel>, currentScreen: LoginScreen): LoginScreen {
    const customSignupUrl = this.configService.get().customSignUpUrl;
    const customSignup = this.configService.get().customSignUp;
    const hasCode = context.getState().code !== null;

    if (customSignupUrl) {
      const [ openWindow ] = createWindowDeferred();
      openWindow(this.getUrlWithUtmParams(customSignupUrl));
      return currentScreen;
    }

    if (customSignup && !hasCode) {
      return 'signup-code';
    }

    return 'signup';
  }

  // TODO: Refactor and move this and header.service:getUrlWithUtmParams into new service or something
  private getUrlWithUtmParams(url: string): string {
    const utmParams = this.urlStateService.getAndStoreParams('utm');
    if (utmParams.size === 0) {
      return url;
    }

    const searchParams = new URLSearchParams(window.location.search);

    utmParams.forEach((v, k) => {
      if (!searchParams.has(k)) {
        searchParams.set(k, v);
      }
    });

    return `${url}?${searchParams.toString()}`;
  }
}
